import React, { useState, useEffect } from "react";
import ManagersNav from "./managernav";
import { MinusCircleIcon, PlusIcon, UserGroupIcon, CheckIcon } from "@heroicons/react/20/solid";

// 🎨 Helper functions for management levels
const getManagementLabel = (level) => {
  switch (level) {
    case 2:
      return "Team Manager";
    case 3:
      return "Senior Management";
    case 4:
      return "Director";
    default:
      return "Employee";
  }
};

const getManagementColor = (level) => {
  switch (level) {
    case 2:
      return "bg-blue-500";
    case 3:
      return "bg-green-500";
    case 4:
      return "bg-purple-500";
    default:
      return "bg-gray-500";
  }
};

// 👤 Simple staff card component
const StaffCard = ({ staff, onAssign, departments, isSelected, onSelect }) => {
  const [selectedDeptId, setSelectedDeptId] = useState('');

  const handleAssign = () => {
    if (selectedDeptId) {
      const selectedDept = departments.find(d => d.id.toString() === selectedDeptId);
      if (selectedDept) {
        onAssign(staff, selectedDept);
        setSelectedDeptId(''); // Reset after assignment
      }
    }
  };

  return (
    <div className={`p-4 rounded-lg border-2 transition-all ${
      isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white'
    } hover:shadow-md`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(staff.id, e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <div>
            <h4 className="font-medium text-gray-900">{staff.Name}</h4>
            <p className="text-sm text-gray-500">{staff.Role}</p>
          </div>
        </div>
        <span
          className={`px-2 py-1 text-xs text-white rounded-md ${getManagementColor(staff.ManagementLevel)}`}
        >
          {getManagementLabel(staff.ManagementLevel)}
        </span>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={selectedDeptId}
          onChange={(e) => setSelectedDeptId(e.target.value)}
          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="">Select Department</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.Name}
            </option>
          ))}
        </select>
        <button
          onClick={handleAssign}
          disabled={!selectedDeptId}
          data-component="staff-assignment-card"
          data-time-saved="2"
          data-feature="assign_staff_put"
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <CheckIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// 🏢 Main component
export default function Members() {
  const [departments, setDepartments] = useState([]);
  const [unassignedStaff, setUnassignedStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [bulkDeptId, setBulkDeptId] = useState('');
  const [zoom, setZoom] = useState(1);

  // Fetch departments and staff
  const fetchData = async () => {
        try {
      const deptResponse = await fetch("https://api.insightos.co.uk/api/getdepartments", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',

      });

      if (!deptResponse.ok) throw new Error(`HTTP error! status: ${deptResponse.status}`);

      const deptData = await deptResponse.json();
      const fetchedDepartments = deptData.data.map((dept) => ({
        ...dept,
        staff: Array.isArray(dept.staff) ? dept.staff : [],
      }));

      const assignedStaffIds = fetchedDepartments.flatMap((dept) => dept.staff.map((s) => s.id));

      const staffResponse = await fetch("https://api.insightos.co.uk/api/staff", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',

      });

      if (!staffResponse.ok) throw new Error(`HTTP error! status: ${staffResponse.status}`);

      const staffData = await staffResponse.json();
      const unassigned = staffData.data.filter((s) => !assignedStaffIds.includes(s.id));

      setUnassignedStaff(unassigned);
      setDepartments(fetchedDepartments);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Move staff between departments
  const moveStaff = async (staff, targetDept) => {
    // Update UI instantly - create new objects to avoid mutation
    setDepartments((prevDepts) =>
      prevDepts.map((dept) => ({
        ...dept,
        staff: dept.staff.filter((s) => s.id !== staff.id).concat(
          targetDept && dept.id === targetDept.id ? [staff] : []
        )
      }))
    );
  
    if (!targetDept) {
      setUnassignedStaff((prev) => [...prev, staff]);
    } else {
      setUnassignedStaff((prev) => prev.filter((s) => s.id !== staff.id));
    }
  
    // Save changes via API
    try {
      const response = await fetch("https://api.insightos.co.uk/api/updatestaffdepartment", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({
          staffId: staff.id,
          newDepartment: targetDept ? targetDept.Name : null,
          role: staff.Role
        }),
      });
  
      if (!response.ok) throw new Error(`Failed to update department: ${response.status}`);
    } catch (error) {
      console.error(error);
    }
  };

  // Handle selection of individual staff members
  const handleStaffSelect = (staffId, isSelected) => {
    setSelectedStaff(prev => 
      isSelected 
        ? [...prev, staffId]
        : prev.filter(id => id !== staffId)
    );
  };

  // Handle bulk assignment
  const handleBulkAssign = () => {
    if (!bulkDeptId || selectedStaff.length === 0) return;

    const targetDept = departments.find(d => d.id.toString() === bulkDeptId);
    if (!targetDept) return;

    const staffToMove = unassignedStaff.filter(staff => selectedStaff.includes(staff.id));
    
    staffToMove.forEach(staff => {
      moveStaff(staff, targetDept);
    });

    setSelectedStaff([]);
    setBulkDeptId('');
  };

  // Remove staff from department (move to unassigned)
  const removeFromDepartment = async (staff, department) => {
    await moveStaff(staff, null);
  };
  
  

  return (
    <div className="min-h-screen bg-gray-50">
      <ManagersNav />
      
      {/* Header */}
      <div className="bg-indigo-600 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <UserGroupIcon className="h-8 w-8 text-white" />
              <h1 className="text-2xl font-semibold text-white">My Teams</h1>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setZoom(Math.min(zoom + 0.1, 2))}
                type="button"
                className="rounded-full bg-white p-2 text-emerald-500 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              >
                <PlusIcon aria-hidden="true" className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setZoom(Math.max(zoom - 0.1, 0.5))}
                type="button"
                className="rounded-full bg-white p-2 text-red-500 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              >
                <MinusCircleIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
          
          {/* Unassigned Staff Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Unassigned Staff</h2>
                <p className="text-sm text-gray-500 mt-1">{unassignedStaff.length} staff members</p>
              </div>
              
              {/* Bulk Assignment Controls */}
              {selectedStaff.length > 0 && (
                <div className="px-6 py-4 bg-indigo-50 border-b border-gray-200">
                  <div className="flex flex-col space-y-3">
                    <p className="text-sm font-medium text-indigo-900">
                      {selectedStaff.length} staff selected
                    </p>
                    <select
                      value={bulkDeptId}
                      onChange={(e) => setBulkDeptId(e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.Name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleBulkAssign}
                      disabled={!bulkDeptId}
                      data-component="staff-assignment-bulk"
                      data-time-saved="2"
                      data-feature="assign_selected_staff_put"
                      className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Assign Selected
                    </button>
                  </div>
                </div>
              )}
              
              {/* Staff List */}
              <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                {unassignedStaff.length === 0 ? (
                  <div className="text-center py-8">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">All staff are assigned to departments</p>
                  </div>
                ) : (
                  unassignedStaff.map((staff) => (
                    <StaffCard
                      key={staff.id}
                      staff={staff}
                      onAssign={moveStaff}
                      departments={departments}
                      isSelected={selectedStaff.includes(staff.id)}
                      onSelect={handleStaffSelect}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Department Columns */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {departments.map((dept) => (
                <div key={dept.id} className="bg-white rounded-lg shadow-md">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">{dept.Name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{dept.staff.length} staff members</p>
                  </div>
                  <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
                    {dept.staff.length === 0 ? (
                      <div className="text-center py-8">
                        <UserGroupIcon className="mx-auto h-8 w-8 text-gray-400" />
                        <p className="text-sm text-gray-500">No staff assigned</p>
                      </div>
                    ) : (
                      dept.staff.map((staff) => (
                        <div key={staff.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex items-center space-x-3">
                            <div>
                              <h4 className="font-medium text-gray-900">{staff.Name}</h4>
                              <p className="text-sm text-gray-500">{staff.Role}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 text-xs text-white rounded-md ${getManagementColor(staff.ManagementLevel)}`}
                            >
                              {getManagementLabel(staff.ManagementLevel)}
                            </span>
                            <button
                              onClick={() => removeFromDepartment(staff, dept)}
                              data-component="staff-assignment-department"
                              data-feature="remove_staff_put"
                              data-time-saved="2"
                              className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-100 transition-colors"
                              title="Remove from department"
                            >
                              <MinusCircleIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
